/**
 * The web-tracking visitor id minted by the backend's `/visitor.js`.
 *
 * The tracker writes the same value to two places: `localStorage` on the host
 * that loaded it, and a first-party cookie on the registrable domain. The
 * cookie is what lets a clinic's marketing site on a sibling subdomain
 * (`www.clinic.com.au`) share the id with the portal (`portal.clinic.com.au`),
 * where `localStorage` is per-origin and would start empty.
 *
 * Kept dependency-free (no React, no generated types) so the flow hooks and
 * the portal's visitor linking can both read it cheaply, and so its tests do
 * not pay for loading either.
 */
export const VISITOR_ID_KEY = "dapple_visitor_id";

/**
 * Anything longer than this is not an id the tracker minted. The backend
 * validates properly; this only stops an arbitrary cookie value being posted.
 */
const MAX_VISITOR_ID_LENGTH = 128;

const normalise = (value: string | null | undefined): string => {
  const trimmed = (value ?? "").trim();
  if (!trimmed || trimmed.length > MAX_VISITOR_ID_LENGTH) return "";
  return trimmed;
};

/**
 * Reads the id from `localStorage`. Storage access throws in some browsers
 * when site data is blocked, which must read as "no id", never as a crash.
 */
const readFromLocalStorage = (): string => {
  try {
    return normalise(globalThis.window?.localStorage?.getItem(VISITOR_ID_KEY));
  } catch {
    return "";
  }
};

/**
 * Reads the id from the `dapple_visitor_id` cookie, if the document has one.
 */
export const readVisitorIdCookie = (): string => {
  try {
    const cookie = globalThis.document?.cookie;
    if (typeof cookie !== "string" || !cookie) return "";

    const prefix = `${VISITOR_ID_KEY}=`;
    const entry = cookie
      .split(";")
      .map(part => part.trim())
      .find(part => part.startsWith(prefix));
    if (!entry) return "";

    return normalise(decodeURIComponent(entry.slice(prefix.length)));
  } catch {
    // A malformed percent-escape makes decodeURIComponent throw.
    return "";
  }
};

/**
 * The visitor id for this browser: `localStorage` first (what the tracker on
 * this host wrote), then the shared cookie. Returns "" when there is none, or
 * on native, where neither exists.
 */
export const readVisitorId = (): string =>
  readFromLocalStorage() || readVisitorIdCookie();
